import { useState, useCallback } from 'react';
import { graphql, useMutation, useFragment } from 'react-relay';
import { getBuyerId } from 'dibs-cookie-jar';

import { useRefreshMetadataMutation } from './__generated__/useRefreshMetadataMutation.graphql';
import { useRefreshMetadata_nftItem$key } from './__generated__/useRefreshMetadata_nftItem.graphql';

export type RefreshMetadataStatus = null | 'success' | 'inflight';

export const useRefreshMetadata = (
    nftItemRef: useRefreshMetadata_nftItem$key | null = null
): [
    handleRefreshMetadata: () => void,
    refreshMetadataStatus: RefreshMetadataStatus,
    reset: () => void
] => {
    const [refreshMetadataStatus, setRefreshMetadataStatus] = useState<RefreshMetadataStatus>(null);

    const nftItem = useFragment(
        graphql`
            fragment useRefreshMetadata_nftItem on NftItem {
                serviceId
            }
        `,
        nftItemRef
    );

    const [commitRefreshMetadata, refreshInFlight] = useMutation<useRefreshMetadataMutation>(
        graphql`
            mutation useRefreshMetadataMutation(
                $nftItemIds: [String!]
                $userId: String!
                $fetchUser: Boolean!
                $fetchSplitName: Boolean = false
            ) {
                nftRetryItemImport(input: { nftItemIds: $nftItemIds }) {
                    clientMutationId
                    nftItems {
                        ...CreateNftForm_nftItem
                        ...NftItemTileTooltip_nftItem
                    }
                }
            }
        `
    );

    const handleRefreshMetadata = useCallback(() => {
        const userId = getBuyerId(document.cookie);
        if (nftItem?.serviceId) {
            commitRefreshMetadata({
                variables: {
                    nftItemIds: [nftItem?.serviceId],
                    userId,
                    fetchUser: !!userId,
                    fetchSplitName: !!userId,
                },
                onCompleted: () => {
                    setRefreshMetadataStatus('success');
                },
            });
        }
    }, [commitRefreshMetadata, nftItem]);

    const reset = useCallback(() => setRefreshMetadataStatus(null), []);
    const status = refreshInFlight ? 'inflight' : refreshMetadataStatus;

    return [handleRefreshMetadata, status, reset];
};
